import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useDelayedTrue } from 'hooks/useDelayedTrue';
import type { RouteLoadingLayout } from 'utils/routeLoadingLayout';
import styles from './RouteLoadingFallback.module.scss';

const SHOW_SPINNER_DELAY_MS = 150;

type RouteLoadingFallbackProps = {
  variant?: 'full' | 'content';
  layout?: RouteLoadingLayout;
};

function HubPageSkeleton() {
  return (
    <div className={styles.hubSkeleton} aria-hidden>
      <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} />
      <div className={styles.hubCardGrid}>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className={styles.hubCard}>
            <div className={`${styles.skeletonBlock} ${styles.skeletonIcon}`} />
            <div className={styles.hubCardLines}>
              <div
                className={`${styles.skeletonBlock} ${styles.skeletonLineMedium}`}
              />
              <div
                className={`${styles.skeletonBlock} ${styles.skeletonLineLong}`}
              />
              <div
                className={`${styles.skeletonBlock} ${styles.skeletonLineShort}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ListPageSkeleton() {
  return (
    <div className={styles.listSkeleton} aria-hidden>
      <div className={styles.listSkeletonHeader}>
        <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} />
        <div className={`${styles.skeletonBlock} ${styles.skeletonAction}`} />
      </div>
      <div className={`${styles.skeletonBlock} ${styles.skeletonSearch}`} />
      <div className={styles.listSkeletonRows}>
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className={styles.listSkeletonRow}>
            <div
              className={`${styles.skeletonBlock} ${styles.skeletonLineLong}`}
            />
            <div
              className={`${styles.skeletonBlock} ${styles.skeletonLineMedium}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function RouteLoadingFallback({
  variant = 'full',
  layout = 'default',
}: RouteLoadingFallbackProps) {
  const { t } = useTranslation();
  const showSpinner = useDelayedTrue(true, SHOW_SPINNER_DELAY_MS);
  const shellClassName =
    variant === 'content' ? styles.contentShell : styles.fullShell;

  if (layout === 'hub') {
    return (
      <div
        className={shellClassName}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={t(
          'components.RouteLoadingFallback.RouteLoadingFallback.loading_1',
        )}
      >
        <HubPageSkeleton />
      </div>
    );
  }

  if (layout === 'list') {
    return (
      <div
        className={shellClassName}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={t(
          'components.RouteLoadingFallback.RouteLoadingFallback.loading_1',
        )}
      >
        <ListPageSkeleton />
      </div>
    );
  }

  return (
    <div
      className={`${shellClassName} ${styles.defaultFallback}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={t(
        'components.RouteLoadingFallback.RouteLoadingFallback.loading_1',
      )}
    >
      {showSpinner ? (
        <>
          <Loader2 className={styles.spinner} size={28} aria-hidden />
          <span className={styles.loadingLabel}>
            {t(
              'components.RouteLoadingFallback.RouteLoadingFallback.loading_1',
            )}
          </span>
        </>
      ) : null}
    </div>
  );
}

export default RouteLoadingFallback;
